import { Component } from '@theme/component';

/**
 * The reveal styles this component can drive.
 *
 * - `fade`    — the wrapper itself fades and lifts in.
 * - `stagger` — direct children arrive one after another, each offset by
 *               `--reveal-step` multiplied by its own `--reveal-index`.
 * - `words`   — text is split into words that lift in sequence, offset by
 *               `--reveal-step-word`. Element children are carried across
 *               whole so nested markup and line breaks survive.
 *
 * @typedef {'fade' | 'stagger' | 'words'} RevealMode
 */

/**
 * Reveals its contents once scrolled into view.
 *
 * Motion is opt-in and never gates content: with JavaScript unavailable the
 * `no-js` fallback in the colocated stylesheet leaves everything visible, and
 * when the visitor prefers reduced motion the reveal resolves immediately
 * rather than animating.
 *
 * @extends Component
 */
class ScrollRevealComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#arm();
  }

  /** Re-arms after a Section Rendering morph, which replaces our children. */
  updatedCallback() {
    super.updatedCallback();
    this.#arm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disarm();
  }

  /**
   * The requested reveal style, falling back to a plain fade.
   *
   * @returns {RevealMode}
   */
  get #mode() {
    const mode = this.getAttribute('mode');

    return mode === 'stagger' || mode === 'words' ? mode : 'fade';
  }

  /** @returns {boolean} Whether the visitor has asked for reduced motion. */
  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Prepares the contents, then either observes or resolves immediately. */
  #arm() {
    this.#disarm();

    const mode = this.#mode;

    if (mode === 'stagger') this.#index();
    if (mode === 'words') this.#split();

    if (this.#prefersReducedMotion || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    this.classList.remove('is-visible');

    // Matches the mockup's observer: a little past the fold, barely intersecting.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 },
    );

    observer.observe(this);
    this.#observer = observer;
  }

  #disarm() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * Stamps a running index on each staggered child so the stylesheet can offset
   * their transitions. Done here rather than in Liquid so adding a card to a
   * group needs no template edit.
   *
   * The children that stagger are usually nested a level or two below this
   * element — a group block puts its blocks inside a content wrapper — so the
   * host marks the real parent with `data-reveal-items`. Without that marker we
   * fall back to our own children.
   */
  #index() {
    const parent = this.querySelector('[data-reveal-items]') ?? this;
    let index = 0;

    for (const child of parent.children) {
      if (!(child instanceof HTMLElement)) continue;

      child.classList.add('reveal-item');
      child.style.setProperty('--reveal-index', String(index));
      index += 1;
    }
  }

  /**
   * Wraps every word in its own inline-block span. Idempotent — a morph that
   * re-renders already-split markup is left alone.
   *
   * The element holding the words is found in order of preference: an explicit
   * `data-reveal-split` marker, then the headings and paragraphs a rich-text
   * setting produces, and finally this element itself.
   */
  #split() {
    const marked = this.querySelectorAll('[data-reveal-split]');
    const targets = marked.length ? marked : this.querySelectorAll(':scope > :is(h1, h2, h3, h4, h5, h6, p)');

    for (const target of targets.length ? targets : [this]) {
      if (target.querySelector('.reveal-word')) continue;

      this.#splitElement(target);
    }
  }

  /**
   * @param {Element} target - The element whose text should be split.
   */
  #splitElement(target) {
    const out = document.createDocumentFragment();
    let index = 0;

    /**
     * @param {string} text - A single word.
     * @returns {HTMLSpanElement} The word, wrapped and indexed.
     */
    const wrap = (text) => {
      const span = document.createElement('span');

      span.className = 'reveal-word';
      span.style.setProperty('--reveal-index', String(index));
      span.textContent = text;
      index += 1;

      return span;
    };

    for (const node of Array.from(target.childNodes)) {
      if (node.nodeType !== Node.TEXT_NODE) {
        // Elements such as an accented <em> or a <br> are carried across whole,
        // so the gold gradient and any deliberate line break survive.
        if (node instanceof HTMLElement && node.tagName !== 'BR') {
          node.classList.add('reveal-word');
          node.style.setProperty('--reveal-index', String(index));
          index += 1;
        }

        out.appendChild(node);
        continue;
      }

      for (const part of (node.nodeValue ?? '').split(/(\s+)/)) {
        if (!part) continue;

        // Whitespace stays a plain text node so the line still wraps naturally.
        out.appendChild(/^\s+$/.test(part) ? document.createTextNode(part) : wrap(part));
      }
    }

    target.textContent = '';
    target.appendChild(out);
  }
}

if (!customElements.get('scroll-reveal-component')) {
  customElements.define('scroll-reveal-component', ScrollRevealComponent);
}
